import { get, sortBy } from "lodash";
import { Language } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import { logError } from "@/utils/log";
import { toJS } from "mobx";

export interface ShareType {
  sharetypeId: number;
  organizationnumber: string;
  name: string;
  vote: number;
  shareTypeCode: ShareTypeCode;
  historical: boolean;
  shareTypeNames?: ShareTypeName[];
  formattedNames?: FormattedName[];
}

export interface ShareTypeName {
  language: Language;
  name: string;
  separator: string;
  singularName?: string;
}

export interface FormattedName {
  language: Language;
  name: string;
  singular: string;
  plural: string;
}

export type ShareTypeFormatOptions = { singular?: boolean; plain?: boolean };

export enum ShareTypeCode {
  COMMON = "COMMON",
  A = "A",
  B = "B",
  C = "C",
  D = "D",
  E = "E",
  F = "F",
  G = "G",
  H = "H",
  I = "I",
  J = "J",
  UNKNOWN = "UNKNOWN",
  PREFERENCE = "PREFERENCE",
  EXTRAORDINARY = "EXTRAORDINARY",
  EXTRAORDINARY_K = "EXTRAORDINARY_K",
  EXTRAORDINARY_L = "EXTRAORDINARY_L",
  EXTRAORDINARY_M = "EXTRAORDINARY_M",
  EXTRAORDINARY_N = "EXTRAORDINARY_N",
  EXTRAORDINARY_O = "EXTRAORDINARY_O",
  EXTRAORDINARY_P = "EXTRAORDINARY_P",
  EXTRAORDINARY_Q = "EXTRAORDINARY_Q",
  EXTRAORDINARY_R = "EXTRAORDINARY_R",
  EXTRAORDINARY_S = "EXTRAORDINARY_S",
  EXTRAORDINARY_T = "EXTRAORDINARY_T",
  EXTRAORDINARY_U = "EXTRAORDINARY_U",
  EXTRAORDINARY_V = "EXTRAORDINARY_V",
  EXTRAORDINARY_W = "EXTRAORDINARY_W",
  EXTRAORDINARY_X = "EXTRAORDINARY_X",
  EXTRAORDINARY_Y = "EXTRAORDINARY_Y",
  EXTRAORDINARY_Z = "EXTRAORDINARY_Z",
}
export const singularShareTypeLabel: { [key in ShareTypeCode]: TMessage } = {
  [ShareTypeCode.COMMON]: { key: "sharetypecode.singular.COMMON" },
  [ShareTypeCode.A]: { key: "sharetypecode.singular.A" },
  [ShareTypeCode.B]: { key: "sharetypecode.singular.B" },
  [ShareTypeCode.C]: { key: "sharetypecode.singular.C" },
  [ShareTypeCode.D]: { key: "sharetypecode.singular.D" },
  [ShareTypeCode.E]: { key: "sharetypecode.singular.E" },
  [ShareTypeCode.F]: { key: "sharetypecode.singular.F" },
  [ShareTypeCode.G]: { key: "sharetypecode.singular.G" },
  [ShareTypeCode.H]: { key: "sharetypecode.singular.H" },
  [ShareTypeCode.I]: { key: "sharetypecode.singular.I" },
  [ShareTypeCode.J]: { key: "sharetypecode.singular.J" },
  [ShareTypeCode.PREFERENCE]: { key: "sharetypecode.singular.PREFERENCE" },
  [ShareTypeCode.EXTRAORDINARY]: { key: "sharetypecode.singular.EXTRAORDINARY" },
  [ShareTypeCode.EXTRAORDINARY_K]: { key: "sharetypecode.singular.EXTRAORDINARY_K" },
  [ShareTypeCode.EXTRAORDINARY_L]: { key: "sharetypecode.singular.EXTRAORDINARY_L" },
  [ShareTypeCode.EXTRAORDINARY_M]: { key: "sharetypecode.singular.EXTRAORDINARY_M" },
  [ShareTypeCode.EXTRAORDINARY_N]: { key: "sharetypecode.singular.EXTRAORDINARY_N" },
  [ShareTypeCode.EXTRAORDINARY_O]: { key: "sharetypecode.singular.EXTRAORDINARY_O" },
  [ShareTypeCode.EXTRAORDINARY_P]: { key: "sharetypecode.singular.EXTRAORDINARY_P" },
  [ShareTypeCode.EXTRAORDINARY_Q]: { key: "sharetypecode.singular.EXTRAORDINARY_Q" },
  [ShareTypeCode.EXTRAORDINARY_R]: { key: "sharetypecode.singular.EXTRAORDINARY_R" },
  [ShareTypeCode.EXTRAORDINARY_S]: { key: "sharetypecode.singular.EXTRAORDINARY_S" },
  [ShareTypeCode.EXTRAORDINARY_T]: { key: "sharetypecode.singular.EXTRAORDINARY_T" },
  [ShareTypeCode.EXTRAORDINARY_U]: { key: "sharetypecode.singular.EXTRAORDINARY_U" },
  [ShareTypeCode.EXTRAORDINARY_V]: { key: "sharetypecode.singular.EXTRAORDINARY_V" },
  [ShareTypeCode.EXTRAORDINARY_W]: { key: "sharetypecode.singular.EXTRAORDINARY_W" },
  [ShareTypeCode.EXTRAORDINARY_X]: { key: "sharetypecode.singular.EXTRAORDINARY_X" },
  [ShareTypeCode.EXTRAORDINARY_Y]: { key: "sharetypecode.singular.EXTRAORDINARY_Y" },
  [ShareTypeCode.EXTRAORDINARY_Z]: { key: "sharetypecode.singular.EXTRAORDINARY_Z" },
  [ShareTypeCode.UNKNOWN]: { key: "sharetypecode.UNKNOWN" },
};
export const pluralShareTypeLabel: { [key in ShareTypeCode]: TMessage } = {
  [ShareTypeCode.COMMON]: { key: "sharetypecode.COMMON" },
  [ShareTypeCode.A]: { key: "sharetypecode.A" },
  [ShareTypeCode.B]: { key: "sharetypecode.B" },
  [ShareTypeCode.C]: { key: "sharetypecode.C" },
  [ShareTypeCode.D]: { key: "sharetypecode.D" },
  [ShareTypeCode.E]: { key: "sharetypecode.E" },
  [ShareTypeCode.F]: { key: "sharetypecode.F" },
  [ShareTypeCode.G]: { key: "sharetypecode.G" },
  [ShareTypeCode.H]: { key: "sharetypecode.H" },
  [ShareTypeCode.I]: { key: "sharetypecode.I" },
  [ShareTypeCode.J]: { key: "sharetypecode.J" },
  [ShareTypeCode.PREFERENCE]: { key: "sharetypecode.PREFERENCE" },
  [ShareTypeCode.EXTRAORDINARY]: { key: "sharetypecode.EXTRAORDINARY" },
  [ShareTypeCode.EXTRAORDINARY_K]: { key: "sharetypecode.EXTRAORDINARY_K" },
  [ShareTypeCode.EXTRAORDINARY_L]: { key: "sharetypecode.EXTRAORDINARY_L" },
  [ShareTypeCode.EXTRAORDINARY_M]: { key: "sharetypecode.EXTRAORDINARY_M" },
  [ShareTypeCode.EXTRAORDINARY_N]: { key: "sharetypecode.EXTRAORDINARY_N" },
  [ShareTypeCode.EXTRAORDINARY_O]: { key: "sharetypecode.EXTRAORDINARY_O" },
  [ShareTypeCode.EXTRAORDINARY_P]: { key: "sharetypecode.EXTRAORDINARY_P" },
  [ShareTypeCode.EXTRAORDINARY_Q]: { key: "sharetypecode.EXTRAORDINARY_Q" },
  [ShareTypeCode.EXTRAORDINARY_R]: { key: "sharetypecode.EXTRAORDINARY_R" },
  [ShareTypeCode.EXTRAORDINARY_S]: { key: "sharetypecode.EXTRAORDINARY_S" },
  [ShareTypeCode.EXTRAORDINARY_T]: { key: "sharetypecode.EXTRAORDINARY_T" },
  [ShareTypeCode.EXTRAORDINARY_U]: { key: "sharetypecode.EXTRAORDINARY_U" },
  [ShareTypeCode.EXTRAORDINARY_V]: { key: "sharetypecode.EXTRAORDINARY_V" },
  [ShareTypeCode.EXTRAORDINARY_W]: { key: "sharetypecode.EXTRAORDINARY_W" },
  [ShareTypeCode.EXTRAORDINARY_X]: { key: "sharetypecode.EXTRAORDINARY_X" },
  [ShareTypeCode.EXTRAORDINARY_Y]: { key: "sharetypecode.EXTRAORDINARY_Y" },
  [ShareTypeCode.EXTRAORDINARY_Z]: { key: "sharetypecode.EXTRAORDINARY_Z" },
  [ShareTypeCode.UNKNOWN]: { key: "sharetypecode.UNKNOWN" },
};

export const shareTypeCodeName: { [key in ShareTypeCode]: TMessage } = {
  [ShareTypeCode.COMMON]: { key: "sharetypecode.name.COMMON" },
  [ShareTypeCode.A]: { key: "sharetypecode.name.A" },
  [ShareTypeCode.B]: { key: "sharetypecode.name.B" },
  [ShareTypeCode.C]: { key: "sharetypecode.name.C" },
  [ShareTypeCode.D]: { key: "sharetypecode.name.D" },
  [ShareTypeCode.E]: { key: "sharetypecode.name.E" },
  [ShareTypeCode.F]: { key: "sharetypecode.name.F" },
  [ShareTypeCode.G]: { key: "sharetypecode.name.G" },
  [ShareTypeCode.H]: { key: "sharetypecode.name.H" },
  [ShareTypeCode.I]: { key: "sharetypecode.name.I" },
  [ShareTypeCode.J]: { key: "sharetypecode.name.J" },
  [ShareTypeCode.PREFERENCE]: { key: "sharetypecode.name.PREFERENCE" },
  [ShareTypeCode.EXTRAORDINARY]: { key: "sharetypecode.name.EXTRAORDINARY" },
  [ShareTypeCode.EXTRAORDINARY_K]: { key: "sharetypecode.name.EXTRAORDINARY_K" },
  [ShareTypeCode.EXTRAORDINARY_L]: { key: "sharetypecode.name.EXTRAORDINARY_L" },
  [ShareTypeCode.EXTRAORDINARY_M]: { key: "sharetypecode.name.EXTRAORDINARY_M" },
  [ShareTypeCode.EXTRAORDINARY_N]: { key: "sharetypecode.name.EXTRAORDINARY_N" },
  [ShareTypeCode.EXTRAORDINARY_O]: { key: "sharetypecode.name.EXTRAORDINARY_O" },
  [ShareTypeCode.EXTRAORDINARY_P]: { key: "sharetypecode.name.EXTRAORDINARY_P" },
  [ShareTypeCode.EXTRAORDINARY_Q]: { key: "sharetypecode.name.EXTRAORDINARY_Q" },
  [ShareTypeCode.EXTRAORDINARY_R]: { key: "sharetypecode.name.EXTRAORDINARY_R" },
  [ShareTypeCode.EXTRAORDINARY_S]: { key: "sharetypecode.name.EXTRAORDINARY_S" },
  [ShareTypeCode.EXTRAORDINARY_T]: { key: "sharetypecode.name.EXTRAORDINARY_T" },
  [ShareTypeCode.EXTRAORDINARY_U]: { key: "sharetypecode.name.EXTRAORDINARY_U" },
  [ShareTypeCode.EXTRAORDINARY_V]: { key: "sharetypecode.name.EXTRAORDINARY_V" },
  [ShareTypeCode.EXTRAORDINARY_W]: { key: "sharetypecode.name.EXTRAORDINARY_W" },
  [ShareTypeCode.EXTRAORDINARY_X]: { key: "sharetypecode.name.EXTRAORDINARY_X" },
  [ShareTypeCode.EXTRAORDINARY_Y]: { key: "sharetypecode.name.EXTRAORDINARY_Y" },
  [ShareTypeCode.EXTRAORDINARY_Z]: { key: "sharetypecode.name.EXTRAORDINARY_Z" },
  [ShareTypeCode.UNKNOWN]: { key: "sharetypecode.UNKNOWN" },
};

const isShareTypeCode = (s: string): s is ShareTypeCode => s in ShareTypeCode;

export function shareTypeName(shareTypeOrCode: ShareType | ShareTypeCode | string): TMessage {
  if (typeof shareTypeOrCode === "string") {
    if (isShareTypeCode(shareTypeOrCode)) {
      return shareTypeCodeName[shareTypeOrCode];
    } else {
      logError(`Unknown shareTypeCode: ${shareTypeOrCode}`);
      return shareTypeCodeName[ShareTypeCode.UNKNOWN];
    }
  }
  return shareTypeCodeName[shareTypeOrCode.shareTypeCode];
}

export function shareTypeLabel(
  shareTypeOrCode: ShareType | ShareTypeCode | string,
  options?: { singular?: boolean },
): TMessage {
  const labels = options?.singular ? singularShareTypeLabel : pluralShareTypeLabel;
  if (typeof shareTypeOrCode === "string") {
    if (isShareTypeCode(shareTypeOrCode)) {
      return labels[shareTypeOrCode];
    } else {
      logError(`Unknown shareTypeCode: ${shareTypeOrCode}`);
      return labels[ShareTypeCode.UNKNOWN];
    }
  }
  return labels[shareTypeOrCode.shareTypeCode];
}

export function findShareTypeName(
  shareType: ShareType,
  lang: Language,
  options?: ShareTypeFormatOptions,
): string {
  const name = shareType.formattedNames?.find((name) => name.language === lang);
  if (!name) {
    logError(
      `Unable to find sharetype name: lang=${lang} shareTypeNames=${toJS(shareType.shareTypeNames)}`,
    );
    return "";
  }
  if (options?.singular) {
    return name.singular;
  } else if (options?.plain) {
    return name.name;
  } else {
    return name.plural;
  }
}

export function findShareTypeNameMessage(
  shareType: ShareType,
  lang: Language,
  options?: ShareTypeFormatOptions,
): TMessage {
  return { translated: findShareTypeName(shareType, lang, options) };
}

export function lookupShareTypeName(
  shareTypeId: number,
  shareTypes: ShareType[],
  lang: Language,
  options?: ShareTypeFormatOptions,
): string {
  const shareType = shareTypes.find((shareType) => shareType.sharetypeId === shareTypeId);
  if (!shareType) {
    logError(`Unable to find sharetype: sharetypeId=${shareTypeId}`);
    return "";
  }
  return findShareTypeName(shareType, lang, options);
}

const shareTypeCodeIndex = Object.fromEntries(Object.values(ShareTypeCode).map((x, i) => [x, i]));

export function sortByShareType<T>(
  data: T[],
  path: string | number | (string | number)[] | ((x: T) => ShareTypeCode) = "sharetypeCode",
): T[] {
  return sortBy(
    data,
    (x) => shareTypeCodeIndex[typeof path === "function" ? path(x) : get(x, path)],
  );
}
